import { useCallback, memo } from "react";
import {
  Paper,
  Typography,
  Divider,
  Box,
  Button,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Devices,
  Settings,
  Add,
  CreditCard,
  AccountBalance,
  Fence,
  RocketLaunch,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BillingService } from "../../services/BillingService";
import { CONFIG } from "../../config";

const ActionButton = memo(
  ({ icon, label, onClick, to, color, variant = "outlined" }) => {
    const theme = useTheme();
    const buttonColor = color || theme.palette.primary.main;

    return (
      <Button
        variant={variant}
        component={to ? RouterLink : undefined}
        to={to || undefined}
        startIcon={icon}
        onClick={onClick}
        fullWidth
        sx={{
          borderRadius: 2,
          justifyContent: "flex-start",
          textAlign: "left",
          py: 1.5,
          px: 2,
          color: variant === "contained" ? "#fff" : buttonColor,
          borderColor:
            variant === "outlined" ? alpha(buttonColor, 0.5) : undefined,
          backgroundColor:
            variant === "contained" ? buttonColor : "transparent",
          transition: "all 0.2s ease",
          "&:hover": {
            borderColor: variant === "outlined" ? buttonColor : undefined,
            backgroundColor:
              variant === "contained"
                ? alpha(buttonColor, 0.9)
                : alpha(buttonColor, 0.08),
            transform: "translateX(4px)",
          },
        }}
      >
        {label}
      </Button>
    );
  }
);

ActionButton.displayName = "ActionButton";

const QuickActionsPanel = memo(({ canAddDevice, userDB, onAddDeviceClick }) => {
  const { t } = useTranslation("common");
  const theme = useTheme();

  const openBillingPortal = useCallback(async () => {
    const url = await BillingService.getBillingPortal();
    window.location.href = url;
  }, []);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(145deg, rgba(40,40,45,0.9) 0%, rgba(35,35,40,0.8) 100%)"
            : "linear-gradient(145deg, rgba(255,255,255,0.9) 0%, rgba(245,245,245,0.8) 100%)",
        backdropFilter: "blur(10px)",
        border: `1px solid ${
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.1)"
            : "rgba(0,0,0,0.1)"
        }`,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box display="flex" alignItems="center" gap={1.5} mb={2}>
        {/* Top accent bar */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: theme.palette.primary.main,
            opacity: 0.8,
          }}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 40,
            height: 40,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
          }}
        >
          <RocketLaunch />
        </Box>
        <Box>
          <Typography variant="subtitle1" component="h2" fontWeight={600}>
            {t("common.quick_actions")}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t("common.shortcuts", { defaultValue: "Common shortcuts" })}
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ mb: 2, opacity: 0.5 }} />

      <Box display="flex" flexDirection="column" gap={1.5} flex={1}>
        {/* Add Device - Primary action */}
        {canAddDevice && (
          <ActionButton
            icon={<Add />}
            label={t("common.add_device")}
            onClick={onAddDeviceClick}
            color={theme.palette.success.main}
            variant="outlined"
          />
        )}

        <ActionButton
          icon={<Devices />}
          label={t("common.view_all_devices")}
          to="/device"
        />

        <ActionButton
          icon={<Fence />}
          label={t("common.manage_geofences")}
          to="/geofence"
        />

        <ActionButton
          icon={<Settings />}
          label={t("common.account_settings")}
          to="/account"
        />

        {/* Billing Actions - only shown if billing is enabled */}
        {CONFIG.billing_enabled && (
          <>
            {userDB?.stripe_billing_status === "active" ? (
              <ActionButton
                icon={<AccountBalance />}
                label={t("common.billing_portal")}
                onClick={openBillingPortal}
                color="#00c9a7"
              />
            ) : (
              <ActionButton
                icon={<CreditCard />}
                label={t("common.register_card")}
                onClick={openBillingPortal}
              />
            )}
          </>
        )}
      </Box>
    </Paper>
  );
});

QuickActionsPanel.displayName = "QuickActionsPanel";

export default QuickActionsPanel;
